import React, { useMemo } from 'react';
import { Box, Typography, styled, useTheme } from '@mui/material';
import { EBarChart } from '../charts';
import { WaterQualityData, WaterElementData } from '../../types/waterQuality';

interface MineralContentChartProps {
  waterData: WaterQualityData;
}

const SectionTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontWeight: theme.typography.fontWeightMedium,
}));

/**
 * Parses ideal range string like "<20" or "80-120" into numeric bounds for comparison
 */
const parseIdealRange = (idealString: string): { min: number; max: number } => {
  if (idealString.startsWith('<')) {
    const maxValue = parseFloat(idealString.substring(1));
    return { min: 0, max: maxValue };
  }
  
  if (idealString.startsWith('>')) {
    const minValue = parseFloat(idealString.substring(1));
    return { min: minValue, max: 1000 }; // Using 1000 as an arbitrary high value
  }
  
  if (idealString.includes('-')) {
    const [minStr, maxStr] = idealString.split('-');
    return {
      min: parseFloat(minStr),
      max: parseFloat(maxStr)
    };
  }
  
  const value = parseFloat(idealString);
  return { min: value * 0.8, max: value * 1.2 }; // 20% tolerance around ideal value
};

export const MineralContentChart: React.FC<MineralContentChartProps> = ({ waterData }) => {
  const theme = useTheme();
  
  // Transform water data for chart display
  const mineralData = useMemo(() => {
    // Define ideal values for each element
    const elementDataWithIdeal: WaterElementData[] = [
      { name: 'Ca', value: waterData.ca, ideal: 40, unit: 'ppm' },
      { name: 'Mg', value: waterData.mg, ideal: 20, unit: 'ppm' },
      { name: 'K', value: waterData.k, ideal: 10, unit: 'ppm' },
      { name: 'Na', value: waterData.na, ideal: '<20', unit: 'ppm' },
      { name: 'Cl', value: waterData.cl, ideal: '<30', unit: 'ppm' },
      { name: 'SO₄', value: waterData.so4, ideal: 30, unit: 'ppm' },
      { name: 'NO₃', value: waterData.no3, ideal: '<10', unit: 'ppm' },
      { name: 'HCO₃', value: waterData.hco3, ideal: '80-120', unit: 'ppm' },
    ];
    
    // Transform to chart data format
    return elementDataWithIdeal.map(element => {
      // Calculate ideal bounds
      let idealMin = 0;
      let idealMax = 0;
      
      if (typeof element.ideal === 'number') {
        idealMin = element.ideal * 0.8; // 20% below ideal
        idealMax = element.ideal * 1.2; // 20% above ideal
      } else {
        const range = parseIdealRange(element.ideal);
        idealMin = range.min;
        idealMax = range.max;
      }
      
      return {
        name: element.name,
        value: element.value,
        ideal: typeof element.ideal === 'number' ? element.ideal : (idealMin + idealMax) / 2,
        unit: element.unit,
      };
    });
  }, [waterData]);
  
  // Custom tooltip formatter
  const tooltipFormatter = (value: number | string) => {
    return `${value} ppm`;
  };
  
  return (
    <Box sx={{ mb: 4 }}>
      <SectionTitle variant="h6">Mineral Content</SectionTitle>      <EBarChart
        data={mineralData}
        series={[
          {
            dataKey: 'value',
            name: 'Current Level',
            color: theme.palette.primary.main,
          },
          {
            dataKey: 'ideal',
            name: 'Ideal Level',
            color: theme.palette.secondary.main,
          },
        ]}
        height={400}
        xAxisLabel="Minerals"
        yAxisLabel="Concentration (ppm)"
        tooltipFormatter={tooltipFormatter}
        grid={true}
      />
    </Box>
  );
};

export default MineralContentChart;
